import { useState, useEffect } from 'react';
import { Button, Flex, Typography, Switch, InputNumber, Slider, Tooltip } from 'antd';
import {
  InfoCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  UndoOutlined,
  ControlOutlined,
  SyncOutlined,
  ShareAltOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { RadioGroup, ConfigurationProgress, ConfigurationSteps } from 'components/pages/crm-spot/shared-components';
import { QueueLayout } from 'components/pages/crm-spot/layouts/queue-layout';
import styles from './queue-config.module.scss';
import { Queued32 } from '@carbon/icons-react';
import { useSettings } from 'components/pages/crm-spot/hooks/useSettings';
import TestConfigurationModal from './test-configuration-modal';

const { Title, Paragraph, Text } = Typography;

export const QueueConfig = () => {
  const methods = [
    {
      value: 'load-balanced',
      label: 'Load Balanced',
      description: 'Assigns leads based on team member capacity and current workload',
    },
    {
      value: 'smart-distribution',
      label: 'Smart Distribution',
      description: 'Combines round robin with load balancing to prevent any team member from being overloaded',
    },
  ];
  const marks = {
    0: '1',
    50: '50',
    100: '100',
  };
  const [activeTab, setActiveTab] = useState('settings');
  const [isTestModalVisible, setIsTestModalVisible] = useState(false);

  const { settings, handleAppSetting } = useSettings();

  const handleTestConfiguration = () => {
    setIsTestModalVisible(true);
  };

  return (
    <QueueLayout
      title="Queue Configuration"
      label="Control how leads are distributed to your team"
      description="Configure how leads are distributed to your team members. Set up automatic distribution rules, define working hours behavior, and manage queue functionality."
      icon={<ControlOutlined />}>
      {/* Configuration Progress */}
      <ConfigurationProgress
        title="Configuration Progress"
        icon={<SyncOutlined />}
        configuredSettings={
          settings['queue-status']
            ? Object.keys(settings).filter((key) => key.startsWith('queue-') && settings[key]).length
            : 0
        }
        totalSettings={3}
        isEnabled={settings['queue-status']}
        disabledMessage="Queue system disabled"
      />

      {/* Steps display */}
      <ConfigurationSteps
        steps={[
          {
            id: 'queue-status',
            label: 'Queue Status',
            isCompleted: !!settings['queue-status'],
          },
          {
            id: 'distribution-method',
            label: 'Distribution Method',
            isCompleted: !!settings['queue-distribution'],
          },
          {
            id: 'assignment-rules',
            label: 'Assignment Rules',
            isCompleted: !!settings['queue-reassignment-time'],
          },
        ]}
      />

      <div className={styles.tabsContainer}>
        <button
          className={`${styles.tabButton} ${activeTab === 'settings' ? styles.active : styles.inactive}`}
          onClick={() => setActiveTab('settings')}>
          General Settings
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === 'visualization' ? styles.active : styles.inactive}`}
          onClick={() => setActiveTab('visualization')}>
          Queue Visualization
        </button>
      </div>

      {activeTab === 'settings' && (
        <>
          <div className="box">
            <div className={styles.sectionNumber}>1</div>
            <Flex justify="space-between" align="center">
              <div>
                <Flex align="center" gap="small">
                  <Title level={3} style={{ margin: 0 }}>
                    Queue System Status
                  </Title>
                  {settings['queue-status'] ? (
                    <span className={styles.statusBadgeActive}>Active</span>
                  ) : (
                    <span className={styles.statusBadgeInactive}>Inactive</span>
                  )}
                </Flex>
                <Paragraph style={{ margin: '8px 0 0 0', color: '#5F6A7D' }}>
                  Enable or disable the lead distribution system
                </Paragraph>
              </div>
              {/* <Switch checked={queueActive} onChange={setQueueActive} /> */}
              <Switch
                checked={settings['queue-status']}
                onChange={(value) => handleAppSetting('queue-status', value)}
              />
            </Flex>
          </div>

          <div className="box">
            <div className={styles.sectionNumber}>2</div>
            <div className="spacing-mb-16">
              <Flex justify="space-between" gap="small">
                <Flex gap="small" align="center">
                  <Title level={3} style={{ margin: 0 }}>
                    Distribution Settings
                  </Title>
                  <Tooltip title="Configure how leads are distributed to your team">
                    <InfoCircleOutlined className={`${styles.infoIcon} icon-16`} />
                  </Tooltip>
                </Flex>
                <div>
                  <span className={styles.essentialBadge}>Essential</span>
                </div>
              </Flex>
            </div>
            <div className="spacing-p-16 border-lightgray border-radius-8 box-shadow-hover">
              <Flex align="center" className="spacing-mb-16">
                <div className="icon-bg-round-16">
                  <ShareAltOutlined />
                </div>
                <Flex justify="space-between" align="center" gap="small">
                  <Title level={3}>Distribution Method</Title>
                  <Tooltip title="This determines how new leads are allocated among your team members within your configured business hours">
                    <InfoCircleOutlined className={`${styles.infoIcon} icon-16`} />
                  </Tooltip>
                </Flex>
              </Flex>
              <Paragraph className="text-dark-blue-50">
                How should new leads be distributed to your team members?
              </Paragraph>
              <RadioGroup
                options={methods}
                value={settings['queue-distribution']}
                setValue={(value) => handleAppSetting('queue-distribution', value)}
              />

              <div className={styles.noteBox}>
                <span className={styles.noteTitle}>Note:</span>
                All lead distribution respects your configured business hours and team member availability settings.
                Smart Distribution combines intelligent routing with workload monitoring to prevent any team member from
                being overloaded.
              </div>
            </div>
          </div>
          <div className="box">
            <div className={styles.sectionNumber}>3</div>
            <div>
              <Flex justify="space-between" gap="small">
                <Flex gap="small" align="center">
                  <Title level={3} style={{ margin: 0 }}>
                    Lead Assignment Rules
                  </Title>
                  <Tooltip title="These settings control limits and timeouts for lead assignments">
                    <InfoCircleOutlined className={`${styles.infoIcon} icon-16`} />
                  </Tooltip>
                </Flex>
                <div>
                  <span className={styles.essentialBadge}>Essential</span>
                </div>
              </Flex>
            </div>
            <div className={styles.grid}>
              <div className="box">
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignSelf: 'center' }}>
                  <div className="align-self-center">
                    <div className="icon-bg-round-16">
                      <TeamOutlined />
                    </div>
                  </div>
                  <div>
                    <Flex align="center" gap="small">
                      <Title level={3}>Maximum Leads Per Member</Title>
                      <Tooltip title="Limits how many active leads a team member can have at once">
                        <InfoCircleOutlined className={`${styles.infoIcon} icon-16`} />
                      </Tooltip>
                    </Flex>
                    <Paragraph className="text-dark-blue-50">
                      Limit the number of active leads assigned to each member
                    </Paragraph>
                  </div>
                  <Title className="align-self-center" level={3}>
                    {settings['queue-active-leads']}
                  </Title>
                </div>
                <Slider
                  marks={marks}
                  value={settings['queue-active-leads']}
                  onChange={(value) => handleAppSetting('queue-active-leads', value)}
                />
              </div>
              <div className="box">
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', alignSelf: 'center' }}>
                  <div className="align-self-center">
                    <div className="icon-bg-round-16">
                      <ClockCircleOutlined />
                    </div>
                  </div>
                  <div>
                    <Flex align="center" gap="small">
                      <Title level={3}>Reassigment Timeout</Title>
                      <Tooltip title="If a team member doesn't respond to a lead within this time, it will be reassigned">
                        <InfoCircleOutlined className={`${styles.infoIcon} icon-16`} />
                      </Tooltip>
                    </Flex>
                    <Paragraph className="text-dark-blue-50">
                      Time before an uncontacted lead is automatically reassigned
                    </Paragraph>
                  </div>
                  <Flex align="center" justify="space-between" gap="small">
                    <InputNumber
                      min={1}
                      value={settings['queue-reassignment-time']}
                      onChange={(value) => handleAppSetting('queue-reassignment-time', value)}
                    />
                    <span className="text-dark-blue-50">hours</span>
                  </Flex>
                </div>
                <div className={styles.recommendationBox}>
                  <div className={styles.recommendationDot} />
                  <span>Recommended: 4 hours for optimal response times</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'visualization' && (
        <div className={styles.visualizationContainer}>
          <Title level={2} className={styles.visualizationTitle}>
            Queue Flow Visualization
          </Title>
          <Paragraph className={styles.visualizationDescription}>
            This visual representation shows how leads will flow through your queue system based on current settings
          </Paragraph>

          <div className={styles.flowContainer}>
            {/* New Lead Arrives Box */}
            <div className={`${styles.flowBox} ${styles.blue}`}>
              <Tooltip title="Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.">
                <div className={`${styles.iconCircle} ${styles.blue}`}>
                  <Queued32 className={`${styles.flowIcon} ${styles.blue}`} />
                </div>
              </Tooltip>
              <div className={styles.flowTitle}>New Lead Arrives</div>
              <div className={styles.flowDescription}>Lead enters the queue system</div>
            </div>

            {/* Processing Box */}
            <div className={`${styles.flowBox} ${styles.yellow}`}>
              <Tooltip title="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.">
                <div className={`${styles.iconCircle} ${styles.yellow}`}>
                  <ClockCircleOutlined className={`${styles.flowIcon} ${styles.yellow}`} />
                </div>
              </Tooltip>
              <div className={styles.flowTitle}>Processing</div>
              <div className={styles.flowSettings}>
                <div className={`${styles.settingBadge} ${styles.distribution}`}>Distribution: Smart Distribution</div>
                <div className={`${styles.settingBadge} ${styles.hours}`}>After Hours: Hold until business hours</div>
                <div className={styles.settingText}>Max {settings['queue-active-leads']} leads per member</div>
              </div>
              {/* Arrow */}
              <div className={styles.arrowContainer}>
                <ArrowRightOutlined className={styles.arrowIcon} />
              </div>
            </div>

            {/* Team Assignment Box */}
            <div className={`${styles.flowBox} ${styles.green}`}>
              <Tooltip title="Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.">
                <div className={`${styles.iconCircle} ${styles.green}`}>
                  <TeamOutlined className={`${styles.flowIcon} ${styles.green}`} />
                </div>
              </Tooltip>
              <div className={styles.flowTitle}>Team Assignment</div>
              <div className={styles.flowDescription}>Lead assigned to available team member</div>
              <div className={`${styles.settingBadge} ${styles.timeout}`}>4hr timeout</div>
              {/* Arrow */}
              <div className={styles.arrowContainer}>
                <ArrowRightOutlined className={styles.arrowIcon} />
              </div>
            </div>
          </div>

          {/* Expected Outcomes */}
          <div className={styles.outcomesContainer}>
            <div className={styles.outcomesHeader}>
              <CheckCircleOutlined className={styles.checkIcon} />
              <Title level={3} style={{ margin: 0 }}>
                Expected Outcomes
              </Title>
            </div>

            <div className={styles.outcomesList}>
              <div className={styles.outcomeItem}>
                <div className={styles.bulletPoint} />
                <Text>Equitable lead distribution across team members</Text>
              </div>
              <div className={styles.outcomeItem}>
                <div className={styles.bulletPoint} />
                <Text>Faster response times to new leads</Text>
              </div>
              <div className={styles.outcomeItem}>
                <div className={styles.bulletPoint} />
                <Text>Reduced chance of leads being missed or neglected</Text>
              </div>
              <div className={styles.outcomeItem}>
                <div className={styles.bulletPoint} />
                <Text>Automatic reassignment if leads aren't contacted</Text>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="spacing-p-16 spacing-mt-16">
        <Button icon={<PlayCircleOutlined />} type="default" onClick={handleTestConfiguration}>
          Test Configuration
        </Button>
      </div>

      {/* Test Configuration Modal */}
      <TestConfigurationModal
        visible={isTestModalVisible}
        onClose={() => setIsTestModalVisible(false)}
        settings={settings}
      />
    </QueueLayout>
  );
};
